import { t } from 'i18next';
import { debounce } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Card, Tab, Tabs, Typography } from '@mui/material';
import { alpha } from '@mui/system';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useBoolean } from 'src/hooks/use-boolean';

import { PageOptions, SortConfig } from 'src/services/api.types';
import { useGetOrganizationContactsQuery, useRemoveContactMutation } from 'src/services/contacts/contacts.service';
import { Contact } from 'src/services/contacts/contacts.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import { openSingleMailerDialog } from 'src/store/slices/app/appSlice';
import { useAppDispatch } from 'src/store/store';

import ContactsTable from 'src/components/custom-tables/contacts-table/contacts-table';

import AddContactModal from 'src/sections/contacts/modal/add-contact-modal';
import ListsTableView from 'src/sections/lists/lists-table-view';

import { SortOrder } from 'src/services/common.types';
import { EmailMessageTemplateType, MailerTargetType } from 'src/services/email-sync/email-sync.types';
import { TenantType } from 'src/types/enums';

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 10,
};

enum TabValue {
  CONTACTS = 'contacts',
  LISTS = 'lists',
}

export default function ContactsListView() {

  const authContext = useAuthContext();

  const showAddContactModal = useBoolean();

  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const [pageOptions, setPageOptions] = useState<PageOptions>(DEFAULT_PAGE);
  const [filterQuery, setFilterQuery] = useState<string | undefined>();
  const [filterType, setFilterType] = useState<TenantType | undefined>();

  const [selectedTab, setSelectedTab] = useState<TabValue | undefined>();
  const [sortConfig, setSortConfig] = useState<SortConfig>({ sort_by: 'created_at', sort_order: SortOrder.DESC });

  const { currentData: contactsPaginated, isLoading: isRetrievingContacts } = useGetOrganizationContactsQuery(
    {
      org_id: authContext.organization?.id,
      search_query: filterQuery,
      contact_type: filterType,
      page: pageOptions,
      sort_config: sortConfig
    },
    {
      skip: !authContext.organization?.id,
      refetchOnMountOrArgChange: true
    }
  );

  const [removeContact] = useRemoveContactMutation();

  const activeTabs = useMemo(() => {
    switch (authContext.organization?.type) {
      case TenantType.Candidate:
        return [TabValue.CONTACTS];
      case TenantType.Recruiter:
      case TenantType.Client:
        return [TabValue.CONTACTS, TabValue.LISTS];
      default:
        return [TabValue.CONTACTS];
    }
  }, [authContext.organization?.type]);

  // Set default tab
  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (selectedTab) return;

    let defaultTab: TabValue = TabValue.CONTACTS

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(TabValue).includes(existingTab as TabValue) && activeTabs.includes(existingTab as TabValue)) {
      defaultTab = existingTab as TabValue;
    }

    setSelectedTab(defaultTab);

  }, [selectedTab, searchParams, setSearchParams, activeTabs]);

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!selectedTab) return;

    setSearchParams({ "tab": selectedTab }, { replace: true })

  }, [selectedTab, setSearchParams]);

  const onChangePage = useCallback((_event: any, pageNumber: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page: pageNumber + 1,
    }));
  }, []);

  const onChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageOptions((prev) => ({
        ...prev,
        per_page: +event.target.value,
      }));
    },
    []
  );

  const handleFilters = useMemo(
    () => debounce((filter, value: string | TenantType) => {
      if (filter === 'type') {
        setFilterType(value as TenantType);
      } else {
        setFilterQuery(value as string);
      }
    }, 500),
    [setFilterQuery]
  );

  const clearFilters = useCallback(() => {
    setFilterQuery('');
    setFilterType(undefined);
  }, []);

  const handleRemoveContact = useCallback(async (contact: Contact) => {
    try {
      await removeContact({
        contact_id: contact.id,
      }).unwrap();

      enqueueSnackbar(t('contacts.api.delete.success'), { variant: 'success' });
    } catch (e) {
      console.error(e);
      enqueueSnackbar(t('contacts.api.delete.default_error'), { variant: 'error' });
    }
  }, [removeContact]);

  const handleOpenMailer = useCallback((contact: Contact) => {
    dispatch(openSingleMailerDialog({
      emails: [contact.email],
      templateType: EmailMessageTemplateType.CONTACT_ENGAGEMENT,
      templateEntityId: contact.id,
      targetType: MailerTargetType.CONTACT,
      targetId: contact.id
    }));
  }, [dispatch]);

  const handleSortChange = useCallback((config: SortConfig) => {
    setSortConfig(config);
  }, []);

  return (
    <Card>
      <Tabs
        value={selectedTab}
        onChange={(_event, tab) => setSelectedTab(tab)}
        sx={{
          px: 2.5,
          boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
        }}
      >
        {activeTabs.map((tab) => (
          <Tab
            key={tab}
            iconPosition="end"
            value={tab}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {tab}
              </Typography>
            }
          />
        ))}
      </Tabs>

      {
        selectedTab === TabValue.CONTACTS &&
        <ContactsTable
          onFilters={handleFilters}
          clearFilters={clearFilters}
          contacts={contactsPaginated}
          isLoading={isRetrievingContacts}
          pageOptions={pageOptions}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onAddContact={showAddContactModal.onTrue}
          onRemoveContact={handleRemoveContact}
          onSendEmail={handleOpenMailer}
          onSortChange={handleSortChange}
        />
      }

      {
        selectedTab === TabValue.LISTS &&
        <ListsTableView
          entityType={ListEntityType.CONTACT}
        />
      }

      <AddContactModal open={showAddContactModal.value} onClose={showAddContactModal.onFalse} />
     
    </Card>


  );
}
